import type { ComponentType, SVGProps } from "react";
import { useState } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { useNavigate } from "@/hooks/useNavigate";
import { usePagePath } from "@/hooks/usePagePath";
import type { RoutePath } from "@/routes/route-types";
import { appLogoSmIconNew } from "@/assets";
import SidebarMenuIcon from "./SidebarMenuIcon";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  useSidebar,
} from "./ui/sidebar";

export type SideBarItemsProps = {
  title: string;
  url?: RoutePath;
  parent?: RoutePath[];
  anchor?: string;
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  subMenu?: SideBarItemsProps[];
  inActiveBg?: string;
  inActiveTextColor?: string;
  activeBg?: string;
  activeTextColor?: string;
  tag?: "release" | "track" | "album";
  status?: "completed" | "pending";
  hasTopDivider?: boolean;
};

export type MenuProps = {
  key: RoutePath;
  showSubMenu: (path: RoutePath, subMenu?: SideBarItemsProps[]) => boolean;
  children?: MenuProps[];
};

export type AppSidebarProps = {
  icon?: string;
  menu: SideBarItemsProps[];
  backgroundColor?: string;
  menuProps?: MenuProps[];
  enableLastPageVisited?: boolean;
};

const AppSidebar = ({
  menu,
  backgroundColor = "#FFFFFF",
}: AppSidebarProps) => {
  const nav = useNavigate();
  const { rawPath } = usePagePath();
  const { open } = useSidebar();
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const toggleExpanded = (title: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(title)) {
        next.delete(title);
      } else {
        next.add(title);
      }
      return next;
    });
  };

  return (
    <Sidebar
      collapsible="icon"
      className="top-0"
      style={{
        backgroundColor,
      }}
    >
      <SidebarContent
        style={{ backgroundColor }}
        className="border-r border-[#D5DBE0] bg-white px-2 py-2"
      >
        <SidebarGroup className="p-0">
          <SidebarGroupContent>
            <SidebarMenu className="gap-1.5">
              {menu.map(
                (
                  {
                    title,
                    icon: itemIcon,
                    url,
                    parent,
                    subMenu = [],
                    inActiveBg = "transparent",
                    activeBg = "#1CACE0",
                    inActiveTextColor = "#3A4652",
                    activeTextColor = "#FFFFFF",
                  },
                  index
                ) => {
                  const hasSubMenu = subMenu.length > 0;
                  const isExpanded =
                    expandedItems.has(title) ||
                    (hasSubMenu &&
                      !!url &&
                      rawPath.startsWith(`/${url}/`));
                  const isSelected =
                    !!url &&
                    (rawPath === `/${url}` ||
                      rawPath.startsWith(`/${url}/`));

                  return (
                    <li key={index}>
                      <button
                        type="button"
                        onClick={() => {
                          if (hasSubMenu) {
                            toggleExpanded(title);
                          }
                          if (url) {
                            nav({ parent, path: url });
                          }
                        }}
                        className={`flex w-full items-center gap-2 rounded-full px-3 py-2 text-left transition ${
                          isSelected ? "shadow-[inset_0_-1px_0_rgba(0,0,0,0.08)]" : "hover:bg-[#F2F7FA]"
                        } ${open ? "justify-start" : "justify-center px-2"}`}
                        style={{
                          backgroundColor: isSelected ? activeBg : inActiveBg,
                          color: isSelected ? activeTextColor : inActiveTextColor,
                        }}
                      >
                        {itemIcon ? <SidebarMenuIcon icon={itemIcon} active={isSelected} /> : null}
                        {open && (
                          <>
                            <span className="flex-1 truncate text-[11px] font-medium leading-none">
                              {title}
                            </span>
                            {hasSubMenu && (
                              isExpanded
                                ? <ChevronUp size={13} />
                                : <ChevronDown size={13} />
                            )}
                          </>
                        )}
                      </button>

                      {/* SubMenu items */}
                      {hasSubMenu && isExpanded && open && (
                        <ul className="mt-1 flex flex-col gap-0.5">
                          {subMenu.map((subItem, subIndex) => {
                            const isSubSelected =
                              !!subItem.url &&
                              (rawPath === `/${subItem.url}` ||
                                rawPath.startsWith(`/${subItem.url}/`));
                            return (
                              <li key={subIndex}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (subItem.url) {
                                      nav({ path: subItem.url });
                                    }
                                  }}
                                  className={`sidebar-submenu-item ${isSubSelected ? "sidebar-submenu-item--active" : ""}`}
                                >
                                  {subItem.title}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                }
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter
        className="mt-auto border-r border-[#D5DBE0] p-2"
      >
        <a
          href="https://www.sixdaylabs.ai"
          target="_blank"
          rel="noopener noreferrer"
          className="block cursor-pointer"
        >
          <img
            src={appLogoSmIconNew}
            alt="Sidebar logo"
            className="h-auto w-full object-contain transition-opacity hover:opacity-80"
          />
        </a>
      </SidebarFooter>
    </Sidebar>
  );
};

export default AppSidebar;
